import { DI_TOKENS, initializeDIContainer, resolve } from '@src/di/container';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect } from 'react';
import type { IVideoCreatorStore } from '../stores/IVideoCreatorStore';
import type VideoCreatorStore from '../stores/VideoCreatorStore';
import { useStore } from './StoreContext';

const VideoCreatorStoreContext = createContext<IVideoCreatorStore | null>(null);

interface VideoCreatorStoreProviderProps {
  children: ReactNode;
}

export function VideoCreatorStoreProvider({
  children,
}: VideoCreatorStoreProviderProps) {
  const rootStore = useStore();
  const flags = useFlagsCombination(rootStore.userIdentityStore.currentRole);

  // Initialize DI container and resolve VideoCreatorStore
  const videoCreatorStore = React.useMemo(() => {
    // Initialize the DI container with the existing rootStore instance
    initializeDIContainer(rootStore);

    // Resolve VideoCreatorStore from DI container
    return resolve(DI_TOKENS.VIDEO_CREATOR_STORE);
  }, [rootStore]);

  useEffect(() => {
    if (flags.initialized) {
      videoCreatorStore.setFlags(flags);
    }
  }, [videoCreatorStore, flags]);

  return (
    <VideoCreatorStoreContext.Provider value={videoCreatorStore}>
      {children}
    </VideoCreatorStoreContext.Provider>
  );
}

export const useVideoCreatorStore = () => {
  const context = useContext(VideoCreatorStoreContext);
  if (!context) {
    throw new Error(
      'useVideoCreatorStore must be used within a VideoCreatorStoreProvider',
    );
  }
  return context;
};

// Helper type for components that need VideoCreatorStore
export type WithVideoCreatorStore<P = {}> = P & {
  videoCreatorStore?: VideoCreatorStore;
};
